import { useState } from "react";
import { Link } from "react-router-dom";
import RoleBadge from "@/components/screenwalkthrough/RoleBadge";
import { mappingMeta, mappingText } from "@/lib/screenWalkthrough";

const TH = "px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] whitespace-nowrap";

function FieldRow({ f, workspaceId, displayFile, highlight, testid }) {
  const [open, setOpen] = useState(false);
  const mm = mappingMeta(f.mapping);
  return (
    <>
      <tr className={`border-b border-[var(--ml-border)] ${highlight ? "bg-[#e5f0ff]/40" : ""}`} data-testid={testid} data-role={f.role} data-mapping={f.mapping}>
        <td className="px-2 py-1.5 ml-mono text-xs font-semibold whitespace-nowrap">
          <Link to={`/workspaces/${workspaceId}/metadata/field-dependencies?display_file=${encodeURIComponent(displayFile)}&field=${encodeURIComponent(f.field_name)}`} className="hover:underline" data-testid={`${testid}-link`}>
            {f.field_name}
          </Link>
        </td>
        <td className="px-2 py-1.5"><RoleBadge role={f.role} testid={`${testid}-role`} /></td>
        <td className="px-2 py-1.5 ml-mono text-xs whitespace-nowrap" data-testid={`${testid}-db-file`} style={{ color: mm.fg }} title={mm.hint}>
          {mappingText(f)}
        </td>
        <td className="px-2 py-1.5 ml-mono text-xs whitespace-nowrap" data-testid={`${testid}-db-field`}>
          {f.mapping === "proven" && f.database_field ? f.database_field : <span className="text-[var(--ml-text-secondary)]">—</span>}
        </td>
        <td className="px-2 py-1.5 ml-mono text-[10px] uppercase tracking-widest whitespace-nowrap" data-testid={`${testid}-mapping`} style={{ color: mm.fg }}>
          {f.mapping === "proven" ? `${mm.label} · ${f.mapping_confidence}` : mm.label}
        </td>
        <td className="px-2 py-1.5 text-[11px] text-[var(--ml-text-secondary)] whitespace-nowrap" title={f.evidence_source || ""}>display_file_dds</td>
        <td className="px-2 py-1.5 ml-mono text-xs whitespace-nowrap" data-testid={`${testid}-line`}>
          {f.source_line ?? "—"}
          {f.source_excerpt && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="ml-2 text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline" data-testid={`${testid}-dds-toggle`}>
              {open ? "hide DDS" : "DDS"}
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 text-[11px] text-[var(--ml-text-secondary)] min-w-[14rem]" data-testid={`${testid}-notes`}>
          {f.mapping === "ambiguous" && <strong style={{ color: "#8a6d00" }}>Ambiguous mapping — </strong>}{f.notes}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-[var(--ml-border)]" data-testid={`${testid}-dds`}>
          <td colSpan={8} className="px-2 py-1.5">
            <pre className="text-[11px] ml-mono bg-[var(--ml-surface)] border border-[var(--ml-border)] p-2 overflow-x-auto whitespace-pre">{f.source_excerpt}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Iter 47 — fields of one screen step: input-capable fields first and highlighted, output/display fields below. */
export default function StepFieldTable({ title, fields, workspaceId, displayFile, highlight, testid, emptyText }) {
  return (
    <div data-testid={testid} data-count={fields.length}>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">{title} ({fields.length})</div>
      {fields.length === 0 ? (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid={`${testid}-empty`}>{emptyText}</p>
      ) : (
        <div className="overflow-x-auto border border-[var(--ml-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ml-surface)] border-b border-[var(--ml-border)]">
              <tr>
                <th className={TH}>Field</th><th className={TH}>Role</th><th className={TH}>Database file</th><th className={TH}>Database field</th>
                <th className={TH}>Mapping</th><th className={TH}>Evidence</th><th className={TH}>DDS line</th><th className={TH}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => <FieldRow key={f.field_name} f={f} workspaceId={workspaceId} displayFile={displayFile} highlight={highlight} testid={`${testid}-${f.field_name}`} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
